import { ofType } from 'redux-observable';
import { concat, of } from 'rxjs';
import { map, concatMap, switchMap, catchError, withLatestFrom } from 'rxjs/operators';
import { notification } from '../../components';
import { commandRequest } from '../../helpers/request/utils';
import Actions from '../actions';
import { addExtraDimensionToMethodology, editExtraDimensionToMethodology } from '../methodologyDeep/services';
import { applyWorkspaceIdToPayload } from '../methodologyDeep/scope';
import actions from './actions';
import { createExtraDimension, updateExtraDimension, loadAllExtraDimensionsByCompany, loadAllExtraDimensionsByWorkspace } from './service';
import { ErrorMsg, notifyErrorOrFallback } from '../../settings/models/Error';
import type { AppState } from '../redux.model';
import type { MethodologyDeep } from '../methodologyDeep/models';
import type { User } from '../user/models';

const { saveMethodologyDeepSuccess } = Actions;

export const handleCreateExtraDimension = (action$, state$) =>
  action$.pipe(
    ofType(actions.SAVE_EXTRA_DIMENSION),
    map((action: ReturnType<typeof actions.saveExtraDimension>) => action),
    withLatestFrom(
      state$.pipe(
        map((state: AppState) => ({
          currentUser: state.User.currentUser as User,
          methodologyDeep: state.MethodologyDeep.methodologyDeep as MethodologyDeep,
          migratedWrites: (state.App?.systemFlags?.P40_55336_MIGRATED_CONFIGURATION_WRITES ?? '[]') as string
        }))
      )
    ),
    concatMap(([action, { currentUser, methodologyDeep, migratedWrites }]) => {
      const exPayload = action.payload;
      const objToSave = commandRequest(exPayload.extraDimension, currentUser);
      objToSave.payload = applyWorkspaceIdToPayload(objToSave.payload, methodologyDeep?.workspace_id);
      if (action.payload.isNew) {
        return createExtraDimension(objToSave, migratedWrites).pipe(
          concatMap(() => {
            notification('success', 'Resource created!');
            const newMethodologyDeep = addExtraDimensionToMethodology(methodologyDeep, exPayload.inspectionGroupId, [
              exPayload.extraDimension
            ]);
            return concat([actions.saveExtraDimensionSuccess(objToSave.payload), saveMethodologyDeepSuccess(newMethodologyDeep)]);
          }),
          catchError(error => {
            const msg = notifyErrorOrFallback(error, 'Error create resource. Try it later.');
            return of(actions.saveExtraDimensionFailure(msg));
          })
        );
      }
      return updateExtraDimension(objToSave, migratedWrites).pipe(
        concatMap(() => {
          notification('success', 'Resource Edited!');
          const newMethodologyDeep = editExtraDimensionToMethodology(methodologyDeep, objToSave.payload.id, objToSave.payload);
          return concat([actions.saveExtraDimensionSuccess(objToSave.payload), saveMethodologyDeepSuccess(newMethodologyDeep)]);
        }),
        catchError(error => {
          const msg = notifyErrorOrFallback(error, 'Error edited resource. Try it later.');
          return of(actions.saveExtraDimensionFailure(msg));
        })
      );
    })
  );

export const handleLoadAllExtraDimensionsByCompany = (action$, state$) =>
  action$.pipe(
    ofType(actions.LOAD_EXTRA_DIMENSIONS_BY_COMPANY),
    map((action: any) => action.payload),
    switchMap((payload: any) =>
      loadAllExtraDimensionsByCompany(payload.companyId).pipe(
        map(response => {
          return actions.loadExtraDimensionsByCompanySuccess(response.data.content);
        }),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading company crops!');
          return of(actions.loadExtraDimensionsByCompanyFailure(errorMsg));
        })
      )
    )
  );

export const handleLoadAllExtraDimensionsByWorkspace = action$ =>
  action$.pipe(
    ofType(actions.LOAD_EXTRA_DIMENSIONS_BY_WORKSPACE),
    map((action: { payload: { workspaceId: string } }) => action.payload),
    switchMap((payload: { workspaceId: string }) =>
      loadAllExtraDimensionsByWorkspace(payload.workspaceId).pipe(
        map(response => actions.loadExtraDimensionsByCompanySuccess(response.data.content)),
        catchError((error: ErrorMsg) => {
          const errorMsg = notifyErrorOrFallback(error, 'Error loading workspace resources!');
          return of(actions.loadExtraDimensionsByCompanyFailure(errorMsg));
        })
      )
    )
  );
